import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import { Skeleton } from '@material-ui/lab';
import { Collapse, IconButton, Tooltip } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { useCallback } from 'react';
import { globalDateFormat } from 'app/constants';
import PaginationActionsTableOmni from './PaginationActionsTableOmni';

const useStyles = makeStyles(theme => ({
	'@keyframes tableContainerEnter': {
		'0%': { opacity: 0 },
		'100%': { opacity: 1 }
	},
	'@keyframes rowEnter': {
		'0%': {
			opacity: 0,
			transform: 'translateY(6px)'
		},
		'100%': {
			opacity: 1,
			transform: 'translateY(0)'
		}
	},
	'@keyframes emptyFloat': {
		'0%': { transform: 'translateY(0)' },
		'50%': { transform: 'translateY(-10px)' },
		'100%': { transform: 'translateY(0)' }
	},
	paper: {
		backgroundColor: `${theme.palette.background.paper} !important`,
		maxWidth: '100vw',
		borderRadius: '16px !important',
		border: `1px solid ${theme.palette.divider}`,
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden',
		animation: '$tableContainerEnter 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both',
		boxShadow:
			theme.palette.type === 'dark'
				? '0 6px 18px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.25) !important'
				: '0 6px 18px rgba(15,23,42,0.07), 0 2px 6px rgba(15,23,42,0.04) !important'
	},
	header: {
		backgroundColor: '#102239 !important',
		position: 'sticky',
		top: 0,
		zIndex: 10
	},
	tableCell: {
		whiteSpace: 'nowrap',
		padding: '16px 16px 16px 16px',
		borderBottom: `1px solid ${theme.palette.divider}`,
		transition: 'transform 0.3s ease, color 0.3s ease'
	},
	headerCell: {
		color: `${theme.palette.common.white} !important`,
		fontSize: '1.1rem !important',
		backgroundColor: `${theme.palette.background.tableHeader} !important`,
		fontWeight: 600,
		padding: '16px',
		borderBottom: 'none'
	},
	sortLabel: {
		color: `${theme.palette.common.white} !important`,
		'&:hover': {
			color: `${theme.palette.common.white} !important`
		},
		'&.Mui-active': {
			color: `${theme.palette.common.white} !important`
		},
		'& .MuiTableSortLabel-icon': {
			color: `${theme.palette.common.white} !important`,
			transition: 'transform 0.3s ease'
		}
	},
	tableContainer: {
		borderRadius: '16px',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%'
	},
	tableWrapper: {
		overflow: 'auto',
		flex: 1,
		minHeight: 0,
		position: 'relative',
		width: '100%',
		WebkitOverflowScrolling: 'touch',
		touchAction: 'pan-x pan-y',
		'&::-webkit-scrollbar': {
			width: '8px',
			height: '8px'
		},
		'&::-webkit-scrollbar-track': {
			background: theme.palette.divider,
			borderRadius: '4px'
		},
		'&::-webkit-scrollbar-thumb': {
			background: theme.palette.action.disabled,
			borderRadius: '4px',
			'&:hover': {
				background: theme.palette.action.selected
			}
		}
	},
	table: {
		minWidth: '100%',
		fontSize: '18px !important',
		borderCollapse: 'separate',
		borderSpacing: 0
	},
	stickyHeader: {
		position: 'sticky',
		top: 0,
		zIndex: 5,
		backgroundColor: theme.palette.background.tableHeader
	},
	tableRow: {
		opacity: 0,
		animation: '$rowEnter 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) both',
		transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
		cursor: 'pointer',
		'&:hover': {
			// A tinted row (see rowStyle) can supply a darker shade of its own tint
			// via the --row-hover-bg custom property; untinted rows fall back to the
			// default hover color.
			backgroundColor: `var(--row-hover-bg, ${theme.palette.action.hover}) !important`,
			boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
		},
		'&:hover $hoverActionWrapper': {
			opacity: 1
		}
	},
	pagination: {
		position: 'sticky',
		bottom: 0,
		backgroundColor: theme.palette.background.paper,
		zIndex: 5,
		flexShrink: 0,
		borderTop: `1px solid ${theme.palette.divider}`,
		padding: '8px 0'
	},
	checkbox: {
		color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		'&.Mui-checked': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
		},
		'&.MuiCheckbox-indeterminate': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
		}
	},
	collapseButton: {
		transition: 'transform 0.3s ease'
	},
	toolbarWithCollapse: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexShrink: 0,
		borderBottom: `1px solid ${theme.palette.divider}`
	},
	collapseContainer: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		overflow: 'hidden'
	},
	emptyState: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '400px',
		flexDirection: 'column',
		gap: '16px'
	},
	emptyIcon: {
		fontSize: '64px !important',
		color: theme.palette.text.disabled,
		animation: '$emptyFloat 2.2s ease-in-out infinite'
	},
	copyButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: '4px 8px',
		borderRadius: '4px',
		color: 'inherit',
		fontSize: 'inherit',
		textAlign: 'left',
		'&:hover': {
			backgroundColor: theme.palette.action.hover
		}
	},
	viewText: {
		color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
		fontWeight: 500,
		fontSize: '1.2rem',
		cursor: 'pointer',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		'&:hover': {
			color: theme.palette.type === 'dark' ? '#D1D5DB' : '#374151',
			textDecoration: 'underline'
		}
	},
	downloadIcon: {
		cursor: 'pointer',
		color: theme.palette.type === 'dark' ? '#60A5FA' : '#2563EB',
		transition: 'color 0.2s ease',
		'&:hover': {
			color: theme.palette.type === 'dark' ? '#93C5FD' : '#1D4ED8'
		}
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		padding: '4px 12px',
		borderRadius: '16px',
		fontSize: '12px',
		fontWeight: 500,
		whiteSpace: 'nowrap'
	},
	pillIcon: {
		fontSize: '16px !important'
	},
	hoverActionWrapper: {
		opacity: 0,
		transition: 'opacity 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center'
	},
	detailsText: {
		color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
		fontWeight: 500,
		fontSize: '1.2rem',
		cursor: 'pointer',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		'&:hover': {
			color: theme.palette.type === 'dark' ? '#D1D5DB' : '#374151',
			textDecoration: 'underline'
		}
	}
}));

export function descendingComparator(a, b, orderBy) {
	let valA = a[orderBy];
	let valB = b[orderBy];

	if (valA == null) return 1;
	if (valB == null) return -1;

	const numA = Number(valA);
	const numB = Number(valB);
	if (!isNaN(numA) && !isNaN(numB) && String(valA).trim() !== '' && String(valB).trim() !== '') {
		if (numB < numA) return -1;
		if (numB > numA) return 1;
		return 0;
	}

	if (typeof valA === 'string') valA = valA.toLowerCase();
	if (typeof valB === 'string') valB = valB.toLowerCase();

	if (valB < valA) return -1;
	if (valB > valA) return 1;
	return 0;
}

export function getComparator(order, orderBy) {
	return order === 'desc'
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
	const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort, headCells, checkBox, isSorted } =
		props;
	const classes = useStyles(props);
	const theme = useTheme();

	const createSortHandler = property => event => {
		onRequestSort(event, property);
	};

	return (
		<TableHead className={classes.stickyHeader}>
			<TableRow className={classes.header}>
				{checkBox && (
					<TableCell
						style={{ backgroundColor: theme.palette.background.tableHeader }}
						padding="checkbox"
						className={classes.headerCell}
					>
						<Checkbox
							indeterminate={numSelected > 0 && numSelected < rowCount}
							checked={rowCount > 0 && numSelected === rowCount}
							onChange={onSelectAllClick}
							inputProps={{
								'aria-label': 'select all'
							}}
							className={classes.checkbox}
							style={{
								// Header sits on the dark tableHeader background, so the
								// select-all checkbox must be light to stay visible
								// (body-row checkboxes keep the dark secondary color).
								color: theme.palette.common.white
							}}
						/>
					</TableCell>
				)}
				{headCells.map(headCell => (
					<TableCell
						classes={{ root: classes.tableCell }}
						key={headCell.id}
						align={headCell.numeric ? 'center' : 'left'}
						padding={headCell.disablePadding ? 'none' : 'normal'}
						sortDirection={isSorted && orderBy === headCell.id ? order : false}
						className={classes.headerCell}
					>
						{headCell.disableSort ? (
							<span>{headCell.label}</span>
						) : (
							<TableSortLabel
								active={isSorted && orderBy === headCell.id}
								direction={orderBy === headCell.id ? order : 'asc'}
								onClick={createSortHandler(headCell.id)}
								className={classes.sortLabel}
							>
								{headCell.label}
								{isSorted && orderBy === headCell.id ? (
									<Box component="span" sx={visuallyHidden}>
										{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
									</Box>
								) : null}
							</TableSortLabel>
						)}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	numSelected: PropTypes.number,
	onRequestSort: PropTypes.func.isRequired,
	onSelectAllClick: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	rowCount: PropTypes.number.isRequired,
	headCells: PropTypes.array.isRequired
};

function EnhancedTableToolbar(props) {
	const { numSelected, title, collapsible, isCollapsed, onToggleCollapse } = props;
	const classes = useStyles();

	return (
		<Toolbar
			sx={[
				{
					pl: { sm: 2 },
					pr: { xs: 1, sm: 1 }
				},
				numSelected > 0 && {
					bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
				}
			]}
			className={collapsible ? classes.toolbarWithCollapse : ''}
		>
			{numSelected > 0 ? (
				<Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
					{numSelected} selected
				</Typography>
			) : (
				<Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
					{title}
				</Typography>
			)}
			{collapsible && (
				<IconButton onClick={onToggleCollapse} className={classes.collapseButton} size="small">
					{isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
				</IconButton>
			)}
		</Toolbar>
	);
}

EnhancedTableToolbar.propTypes = {
	numSelected: PropTypes.number.isRequired,
	title: PropTypes.string,
	collapsible: PropTypes.bool,
	isCollapsed: PropTypes.bool,
	onToggleCollapse: PropTypes.func
};

function TableSkeleton({ headCells, checkBox, rowsPerPage = 5, fixedHeight, maxTableHeight }) {
	const classes = useStyles();
	const theme = useTheme();

	const skeletonRows = rowsPerPage > 0 ? rowsPerPage : 5;

	return (
		<Box className={classes.collapseContainer}>
			<div
				className={classes.tableWrapper}
				style={fixedHeight ? { maxHeight: maxTableHeight, overflow: 'auto' } : {}}
			>
				<Table className={classes.table} aria-labelledby="tableTitle" size="medium" stickyHeader>
					<TableHead className={classes.stickyHeader}>
						<TableRow className={classes.header}>
							{checkBox && (
								<TableCell
									style={{ backgroundColor: theme.palette.background.tableHeader }}
									padding="checkbox"
									className={classes.headerCell}
								>
									<Skeleton variant="rect" width={24} height={24} animation="wave" />
								</TableCell>
							)}
							{headCells.map(headCell => (
								<TableCell
									key={headCell.id}
									align={headCell.numeric ? 'center' : 'left'}
									className={classes.headerCell}
								>
									<Skeleton variant="text" width="80%" height={24} animation="wave" />
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{[...Array(skeletonRows)].map((_, rowIndex) => (
							<TableRow key={rowIndex}>
								{checkBox && (
									<TableCell padding="checkbox">
										<Skeleton variant="rect" width={24} height={24} animation="wave" />
									</TableCell>
								)}
								{headCells.map((cell, cellIndex) => (
									<TableCell key={cellIndex} align={cell.numeric ? 'center' : 'left'}>
										<Skeleton variant="text" width="90%" height={20} animation="wave" />
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
			<div className={classes.pagination}>
				<div
					style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
				>
					<Skeleton variant="text" width={150} height={30} animation="wave" />
					<Skeleton variant="text" width={200} height={30} animation="wave" />
				</div>
			</div>
		</Box>
	);
}

export function CopyableCell({ value, cellConfig, classes, theme }) {
	const [copied, setCopied] = React.useState(false);

	const {
		tooltip,
		msg,
		dt,
		email,
		truncateLength = 30,
		bold,
		italic,
		fontWeight,
		pill,
		pillConfig,
		numeric
	} = cellConfig || {};

	const getStringValue = () => {
		if (typeof value === 'string') return value;
		if (typeof value === 'number') return String(value);
		return '';
	};

	const handleCopy = e => {
		e.stopPropagation();
		const textToCopy = getStringValue();
		if (textToCopy) {
			copy(textToCopy);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const getTextStyle = () => {
		const style = {};
		if (fontWeight) {
			style.fontWeight = fontWeight;
		} else if (bold) {
			style.fontWeight = 'bold';
		}
		if (italic) style.fontStyle = 'italic';
		return style;
	};

	const hasFormatting = tooltip || msg || dt || email || pill || bold || italic || fontWeight;
	if (React.isValidElement(value) && !hasFormatting) {
		return value;
	}

	const stringValue = getStringValue();
	const textStyle = getTextStyle();

	if (React.isValidElement(value)) {
		if (dt === true) {
			return value;
		}
		if (msg === true) {
			return (
				<Tooltip title={stringValue || 'View'} arrow interactive>
					<span style={textStyle}>{value}</span>
				</Tooltip>
			);
		}
		if (tooltip === true) {
			return (
				<Tooltip title={stringValue || 'View'} arrow>
					<span style={textStyle}>{value}</span>
				</Tooltip>
			);
		}
		if (pill === true && pillConfig) {
			const { defaultColor = theme?.palette?.text?.primary, defaultBgColor = theme?.palette?.action?.hover } =
				pillConfig;
			return (
				<span className={classes.pill} style={{ backgroundColor: defaultBgColor, color: defaultColor }}>
					{value}
				</span>
			);
		}
		return <span style={textStyle}>{value}</span>;
	}

	if (numeric === true && (value === null || value === undefined || value === '')) {
		return <span style={{ color: theme?.palette?.text?.secondary }}>-</span>;
	}
	if (pill === true && pillConfig) {
		const {
			mapping,
			defaultColor = theme?.palette?.text?.primary,
			defaultBgColor = theme?.palette?.action?.hover
		} = pillConfig;

		let pillData = {
			label: stringValue || '-',
			color: defaultColor,
			bgColor: defaultBgColor,
			icon: null
		};

		if (typeof mapping === 'function') {
			const result = mapping(value, cellConfig.row);
			pillData = { ...pillData, ...result };
		} else if (mapping && mapping[value] !== undefined) {
			pillData = { ...pillData, ...mapping[value] };
		}

		return (
			<span
				className={classes.pill}
				style={{
					backgroundColor: pillData.bgColor,
					color: pillData.color
				}}
			>
				{pillData.icon && <span className={classes.pillIcon}>{pillData.icon}</span>}
				{pillData.label}
			</span>
		);
	}

	if (dt === true) {
		const formattedDate = value ? moment(value).format(globalDateFormat) : '-';
		return <span style={{ color: theme?.palette?.text?.primary, ...textStyle }}>{formattedDate}</span>;
	}

	if (email === true && stringValue) {
		if (!stringValue || stringValue.trim() === '') {
			return <span style={{ color: theme?.palette?.text?.secondary }}>-</span>;
		}
		return (
			<Tooltip title={copied ? 'Copied!' : 'Copy'} arrow>
				<button
					className={classes.copyButton}
					onClick={handleCopy}
					style={{
						textDecoration: 'underline',
						color: theme?.palette?.text?.primary,
						...textStyle
					}}
				>
					{stringValue}
				</button>
			</Tooltip>
		);
	}

	if (msg === true && stringValue) {
		if (!stringValue || stringValue.trim() === '') {
			return <span style={{ color: theme?.palette?.text?.secondary }}>-</span>;
		}

		const shouldTruncate = stringValue.length > truncateLength;
		const displayValue = shouldTruncate ? `${stringValue.substring(0, truncateLength)}...` : stringValue;

		return (
			<Tooltip title={copied ? 'Copied!' : stringValue} arrow interactive>
				<button className={classes.copyButton} onClick={handleCopy} style={textStyle}>
					{displayValue}
				</button>
			</Tooltip>
		);
	}

	if (tooltip === true && stringValue) {
		return (
			<Tooltip title={copied ? 'Copied!' : 'Copy'} arrow>
				<button className={classes.copyButton} onClick={handleCopy} style={textStyle}>
					{stringValue}
				</button>
			</Tooltip>
		);
	}

	return <span style={textStyle}>{value}</span>;
}

CopyableCell.propTypes = {
	value: PropTypes.any,
	cellConfig: PropTypes.shape({
		tooltip: PropTypes.bool,
		msg: PropTypes.bool,
		dt: PropTypes.bool,
		email: PropTypes.bool,
		truncateLength: PropTypes.number,
		bold: PropTypes.bool,
		italic: PropTypes.bool,
		fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		pill: PropTypes.bool,
		pillConfig: PropTypes.shape({
			mapping: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
			defaultColor: PropTypes.string,
			defaultBgColor: PropTypes.string
		}),
		numeric: PropTypes.bool
	}),
	classes: PropTypes.object,
	theme: PropTypes.object
};

function TableOmniNewAnimated(
	{
		rows,
		headCells,
		title,
		tableName,
		checkBox,
		selectedRows,
		setSelectedRows,
		setCurrentParams,
		perPageRows,
		curPage,
		totalItems,
		onEditClick,
		onViewClick,
		onDeleteClick,
		onDownloadClick,
		isLoadingPagination,
		rowKey,
		height = '100%',
		rowsPerPageOptions = [5, 10, 25],
		loading = false,
		collapsible = false,
		defaultCollapsed = false,
		emptyMessage = 'No Data Found!',
		fixedHeight = false,
		maxTableHeight = '500px',
		hoverAction = null,
		onRowClick,
		onDetailsClick,
		// Optional per-row style hook: receives the row and returns a style object
		// merged onto the <TableRow> (e.g. a background tint driven by row status).
		rowStyle
	},
	props
) {
	const classes = useStyles(props);
	const theme = useTheme();

	const [order, setOrder] = React.useState('desc');
	const [orderBy, setOrderBy] = React.useState(headCells[0].id);
	const [isSorted, setIsSorted] = React.useState(false);
	const [page, setPage] = React.useState(curPage || 0);
	const [rowsPerPage, setRowsPerPage] = React.useState(perPageRows || 5);
	const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed);
	const [showEmptyState, setShowEmptyState] = React.useState(false);

	React.useEffect(() => {
		if (!loading && rows.length === 0) {
			const timer = setTimeout(() => setShowEmptyState(true), 300);
			return () => clearTimeout(timer);
		}
		setShowEmptyState(false);
	}, [loading, rows.length]);

	React.useEffect(() => {
		if (curPage !== undefined && curPage !== page) {
			setPage(curPage);
		}
	}, [curPage]);

	React.useEffect(() => {
		if (perPageRows !== undefined && perPageRows !== rowsPerPage) {
			setRowsPerPage(perPageRows);
		}
	}, [perPageRows]);

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc';
		const newOrder = isAsc ? 'desc' : 'asc';
		setOrder(newOrder);
		setOrderBy(property);
		setIsSorted(true);
		if (totalItems && setCurrentParams) {
			setPage(0);
			setCurrentParams(prev => ({ ...prev, page: 0, sortBy: property, sortOrder: newOrder }));
		}
	};

	const getRowKey = useCallback(
		row => {
			if (!rowKey) return row.id;
			if (typeof rowKey === 'function') return rowKey(row);
			return row[rowKey];
		},
		[rowKey]
	);

	const handleSelectAllClick = event => {
		if (event.target.checked) {
			const newSelected = rows.map(n => getRowKey(n));
			setSelectedRows(newSelected);
			return;
		}
		setSelectedRows([]);
	};

	const handleClick = (event, key) => {
		const selectedIndex = selectedRows.indexOf(key);
		let newSelected = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedRows, key);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedRows.slice(1));
		} else if (selectedIndex === selectedRows.length - 1) {
			newSelected = newSelected.concat(selectedRows.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selectedRows.slice(0, selectedIndex),
				selectedRows.slice(selectedIndex + 1)
			);
		}
		setSelectedRows(newSelected);
	};

	const handleChangePage = (event, newPage) => {
		if (rowsPerPage === -1) return;
		setPage(newPage);
		if (setCurrentParams) {
			setCurrentParams(prev => ({ ...prev, limit: rowsPerPage, page: newPage }));
		}
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		if (setCurrentParams) {
			setCurrentParams(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 0 }));
		}
	};

	const handleToggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	const visibleRows = React.useMemo(() => {
		const sortedRows = isSorted ? [...rows].sort(getComparator(order, orderBy)) : rows;

		if (totalItems) {
			return sortedRows;
		}
		if (rowsPerPage === -1) {
			return sortedRows;
		}
		return sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
	}, [totalItems, page, rowsPerPage, rows, order, orderBy, isSorted]);

	const getValue = (row, id, cellConfig) => {
		if (cellConfig?.customRow) {
			return cellConfig.customRow(row);
		}

		const keys = id.split('.');
		const value = keys.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), row);

		if (id === 'view') {
			const displayLabel = cellConfig?.label || 'VIEW';
			return (
				<span
					className={classes.viewText}
					onClick={e => {
						e.stopPropagation();
						onViewClick(row);
					}}
				>
					{displayLabel}
				</span>
			);
		}
		if (id === 'edit') {
			return (
				<EditIcon
					style={{ cursor: 'pointer' }}
					fontSize="large"
					onClick={e => {
						e.stopPropagation();
						onEditClick(row);
					}}
				/>
			);
		}
		if (id === 'delete') {
			return (
				<DeleteIcon
					style={{ cursor: 'pointer' }}
					fontSize="large"
					onClick={e => {
						e.stopPropagation();
						onDeleteClick(row);
					}}
				/>
			);
		}
		if (id === 'download') {
			return (
				<GetAppIcon
					style={{ cursor: 'pointer' }}
					fontSize="large"
					onClick={e => {
						e.stopPropagation();
						if (onDownloadClick) {
							onDownloadClick(row);
						}
					}}
				/>
			);
		}
		if (id === 'details') {
			const displayLabel = cellConfig?.label || 'DETAILS';
			return (
				<span
					className={classes.detailsText}
					onClick={e => {
						e.stopPropagation();
						onDetailsClick && onDetailsClick(row);
					}}
				>
					{displayLabel}
				</span>
			);
		}

		return value;
	};

	if (showEmptyState) {
		return (
			<Box sx={{ width: '100%', height }} className={classes.tableContainer}>
				<Paper className={classes.paper} sx={{ width: '100%', mb: 2, boxShadow: 'none' }}>
					{tableName && (
						<EnhancedTableToolbar
							numSelected={0}
							title={title}
							collapsible={collapsible}
							isCollapsed={isCollapsed}
							onToggleCollapse={handleToggleCollapse}
						/>
					)}
					<div className={classes.emptyState}>
						<SearchOffOutlinedIcon className={classes.emptyIcon} />
						<Typography
							style={{
								color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
								fontSize: 16,
								fontWeight: 500
							}}
						>
							{emptyMessage || 'No Data Found'}
						</Typography>
					</div>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', height }} className={classes.tableContainer}>
			<Paper className={classes.paper} sx={{ width: '100%', mb: 0, boxShadow: 'none' }}>
				{tableName && (
					<EnhancedTableToolbar
						numSelected={selectedRows?.length || 0}
						title={title}
						collapsible={collapsible}
						isCollapsed={isCollapsed}
						onToggleCollapse={handleToggleCollapse}
					/>
				)}

				<Collapse in={!isCollapsed} timeout="auto" className={classes.collapseContainer}>
					{loading || rows.length === 0 ? (
						<TableSkeleton
							headCells={headCells}
							checkBox={checkBox}
							rowsPerPage={rowsPerPage > 0 ? rowsPerPage : 5}
							fixedHeight={fixedHeight}
							maxTableHeight={maxTableHeight}
						/>
					) : (
						<>
							<div
								className={classes.tableWrapper}
								style={fixedHeight ? { maxHeight: maxTableHeight, overflow: 'auto' } : {}}
							>
								<Table
									className={classes.table}
									aria-labelledby="tableTitle"
									size="medium"
									stickyHeader
								>
									<EnhancedTableHead
										numSelected={selectedRows?.length}
										order={order}
										orderBy={orderBy}
										onSelectAllClick={handleSelectAllClick}
										onRequestSort={handleRequestSort}
										rowCount={rows.length}
										headCells={headCells}
										checkBox={checkBox}
										isSorted={isSorted}
									/>
									<TableBody>
										{visibleRows.map((row, index) => {
											const key = getRowKey(row);
											const isItemSelected = selectedRows?.includes(key);
											const labelId = `enhanced-table-checkbox-${index}`;
											const staggerDelay = `${Math.min(index, 15) * 25}ms`;

											return (
												<TableRow
													hover
													onClick={event => {
														if (event.currentTarget.dataset.scrolled === 'true') {
															event.currentTarget.dataset.scrolled = 'false';
															return;
														}
														if (checkBox) {
															handleClick(event, key);
														} else if (onRowClick) {
															onRowClick(row);
														}
													}}
													onTouchMove={e => {
														e.currentTarget.dataset.scrolled = 'true';
													}}
													onTouchStart={e => {
														e.currentTarget.dataset.scrolled = 'false';
													}}
													role="checkbox"
													aria-checked={isItemSelected}
													tabIndex={-1}
													key={key}
													selected={isItemSelected}
													className={classes.tableRow}
													style={{
														animationDelay: staggerDelay,
														...(rowStyle ? rowStyle(row) : {})
													}}
												>
													{checkBox && (
														<TableCell padding="checkbox" className={classes.tableCell}>
															<Checkbox
																checked={isItemSelected}
																inputProps={{
																	'aria-labelledby': labelId
																}}
																className={classes.checkbox}
																style={{
																	color:
																		theme.palette.secondary?.value ||
																		theme.palette.secondary?.main ||
																		'#fff'
																}}
															/>
														</TableCell>
													)}
													{headCells.map(cell => {
														const cellValue = getValue(row, cell.id, cell);

														return (
															<TableCell
																classes={{ root: classes.tableCell }}
																sx={{ fontSize: '1.1rem' }}
																key={cell.id}
																align={cell.numeric ? 'center' : 'left'}
															>
																<div style={{ position: 'relative' }}>
																	<CopyableCell
																		value={cellValue}
																		cellConfig={{ ...cell, row }}
																		classes={classes}
																		theme={theme}
																	/>
																	{hoverAction &&
																		hoverAction.columnId === cell.id && (
																			<div
																				className={classes.hoverActionWrapper}
																				onClick={e => {
																					e.stopPropagation();
																					if (hoverAction.onClick)
																						hoverAction.onClick(row);
																				}}
																				style={{
																					position: 'absolute',
																					left: '8px',
																					top: '100%',
																					marginTop: '2px'
																				}}
																			>
																				{typeof hoverAction.render ===
																				'function'
																					? hoverAction.render(row)
																					: hoverAction.render}
																			</div>
																		)}
																</div>
															</TableCell>
														);
													})}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
							{!isLoadingPagination && (
								<div className={classes.pagination}>
									<TablePagination
										rowsPerPageOptions={rowsPerPageOptions}
										component="div"
										count={totalItems || rows.length}
										rowsPerPage={rowsPerPage === -1 ? totalItems || rows.length : rowsPerPage}
										page={page}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										ActionsComponent={PaginationActionsTableOmni}
									/>
								</div>
							)}
						</>
					)}
				</Collapse>
			</Paper>
		</Box>
	);
}

TableOmniNewAnimated.propTypes = {
	rows: PropTypes.array.isRequired,
	headCells: PropTypes.array.isRequired,
	title: PropTypes.string,
	height: PropTypes.string,
	loading: PropTypes.bool,
	collapsible: PropTypes.bool,
	defaultCollapsed: PropTypes.bool,
	emptyMessage: PropTypes.string,
	onDownloadClick: PropTypes.func,
	fixedHeight: PropTypes.bool,
	maxTableHeight: PropTypes.string,
	hoverAction: PropTypes.shape({
		columnId: PropTypes.string.isRequired,
		render: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
		onClick: PropTypes.func
	}),
	onRowClick: PropTypes.func,
	rowStyle: PropTypes.func
};

export default TableOmniNewAnimated;
